import { Dialog } from "@angular/cdk/dialog";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, from, Observable } from "rxjs";
import {
    debounceTime,
    filter,
    map,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from "rxjs/operators";

import { VtoCategory, VtoProduct, VtoSubcategory } from "@hermes/api-model-vto";

import {
    catchSdkError,
    checkTimeOut,
    fetchLocalizedVtoCategories,
    fetchLocalizedVtoCategoriesSuccess,
    fetchVtoConfiguration,
    fetchVtoConfigurationSuccess,
    launchCamera,
    learnMoreRedirect,
} from "../actions/vto.actions";
import { VTO_CONFIGURATION_UNDEFINED } from "../constants/vto.constant";
import {
    getProductVariantFromList,
    getRouteParametersWithoutLocation,
    isVtoProductNotUndefined,
} from "../helpers/vto.helper";
import {
    LocalizedVtoCategory,
    LocalizedVtoSubcategory,
    NO_SELECT,
} from "../model/localized-vto-category.model";
import { VtoBeautyState } from "../reducers/vto-beauty-state";
import {
    selectLocalizedVtoCategories,
    selectModalStatusState,
    selectVtoConfiguration,
} from "../selectors/vto.selectors";
import { DeprecatedVtoService } from "../services/vto.service";
import { VtoSdkError } from "../types/vto.types";

const TIMEOUT_ERROR_MS = 40_000;
const TIMEOUT_VTO_CONFIG_ERROR_MS = 2000;

@Injectable()
export class VtoEffects {
    public fetchVtoSkuAvailable$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchVtoConfiguration),
            take(1),
            withLatestFrom(this.store.select(selectVtoConfiguration)),
            // If no skus available or sdk config undefined, get new configuration from PerfectCorp
            filter(
                ([, vtoConfig]) => vtoConfig === VTO_CONFIGURATION_UNDEFINED,
            ),
            switchMap(() => this.deprecatedVtoService.getVtoConfiguration()),
            switchMap((vtoConfig) => [
                fetchVtoConfigurationSuccess({
                    vtoConfig,
                }),
            ]),
        ),
    );

    public fetchLocalizedVtoCategories$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchLocalizedVtoCategories),
            withLatestFrom(this.store.select(selectLocalizedVtoCategories)),
            filter(
                ([, existingLocalizedVtoCategories]) =>
                    existingLocalizedVtoCategories.length === 0,
            ),
            switchMap(() => this.deprecatedVtoService.getVtoCategories()),
            switchMap((vtoCategories) =>
                combineLatest(
                    vtoCategories.map((vtoCategory) =>
                        this.addVtoCategoryTranslations(vtoCategory),
                    ),
                ),
            ),
            map((results) =>
                fetchLocalizedVtoCategoriesSuccess({
                    localizedVtoCategories: results,
                }),
            ),
        ),
    );

    public navigateToVtoProduct$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(learnMoreRedirect),
                map((action) => {
                    if (!action.vtoCategory) {
                        return undefined;
                    }
                    return getProductVariantFromList(
                        action.vtoSubcategory
                            ? action.vtoSubcategory?.vtoProducts
                            : [],
                        action.vtoSubcategory?.selectedSkuVariant,
                    );
                }),
                filter(
                    (
                        vtoProduct: VtoProduct | undefined,
                    ): vtoProduct is VtoProduct =>
                        isVtoProductNotUndefined(vtoProduct),
                ),
                map((vtoProduct: VtoProduct) => {
                    // navigate to current page product
                    this.router.navigate(
                        getRouteParametersWithoutLocation(vtoProduct.url),
                        {
                            state: { variant: true },
                        },
                    );
                    // close vtoModal
                    this.dialog.closeAll();
                }),
            ),
        { dispatch: false },
    );

    public fetchExistingLocalizedVtoCategories$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchLocalizedVtoCategories),
            withLatestFrom(this.store.select(selectLocalizedVtoCategories)),
            filter(
                ([, existingLocalizedVtoCategories]) =>
                    existingLocalizedVtoCategories.length > 0,
            ),
            map(([, existingLocalizedVtoCategories]) =>
                fetchLocalizedVtoCategoriesSuccess({
                    localizedVtoCategories: existingLocalizedVtoCategories,
                }),
            ),
        ),
    );

    public launchTimeoutFetchLocalizedVtoCategories$ = createEffect(
        () => () =>
            this.actions$.pipe(
                ofType(fetchLocalizedVtoCategories),
                // start timeout after vto/config call
                debounceTime(TIMEOUT_VTO_CONFIG_ERROR_MS),
                withLatestFrom(this.store.select(selectLocalizedVtoCategories)),
                filter(
                    ([, existingLocalizedVtoCategories]) =>
                        existingLocalizedVtoCategories.length === 0,
                ),
                map(() => checkTimeOut()),
            ),
    );

    public launchTimeoutCamera$ = createEffect(() =>
        this.actions$.pipe(
            ofType(launchCamera),
            switchMap(() =>
                from(
                    navigator.mediaDevices.getUserMedia({
                        video: true,
                    }),
                ),
            ),
            tap((stream: MediaStream) =>
                stream.getTracks().forEach((track) => track.stop()),
            ),
            // start timeout after user authorized video
            debounceTime(TIMEOUT_ERROR_MS),
            map(() => checkTimeOut()),
        ),
    );

    public checkTimeOut$ = createEffect(() =>
        this.actions$.pipe(
            ofType(checkTimeOut),
            withLatestFrom(this.store.select(selectModalStatusState)),
            filter(
                ([, modalStatusState]) =>
                    modalStatusState !== "CAMERA_LOADED" &&
                    modalStatusState !== "ON_ERROR" &&
                    modalStatusState !== "NONE",
            ),
            map(() => catchSdkError({ sdkError: VtoSdkError.ERROR_TIMEOUT })),
        ),
    );

    constructor(
        private actions$: Actions,
        private store: Store<VtoBeautyState>,
        private deprecatedVtoService: DeprecatedVtoService,
        private translateService: TranslateService,
        private router: Router,
        private dialog: Dialog,
    ) {}

    private addVtoCategoryTranslations(
        vtoCategory: VtoCategory,
    ): Observable<LocalizedVtoCategory> {
        return this.translateService
            .get(this.getVtoTranslationKey(vtoCategory.vtoCategoryType))
            .pipe(
                switchMap((translation: string) =>
                    combineLatest(
                        vtoCategory.vtoSubcategories.map(
                            (vtoSubcategory: VtoSubcategory) =>
                                this.addVtoSubcategoryTranslation(
                                    vtoSubcategory,
                                ),
                        ),
                    ).pipe(
                        map((localizedVtoSubcategories) => ({
                            vtoCategoryType: vtoCategory.vtoCategoryType,
                            vtoSubcategories: localizedVtoSubcategories,
                            localizedVtoCategoryType:
                                translation ??
                                this.getVtoTranslationKey(
                                    vtoCategory.vtoCategoryType,
                                ),
                            selectedSubcategoryType: NO_SELECT,
                        })),
                    ),
                ),
            );
    }

    private addVtoSubcategoryTranslation(
        vtoSubcategory: VtoSubcategory,
    ): Observable<LocalizedVtoSubcategory> {
        return this.translateService
            .get(
                this.getVtoTranslationKey(
                    vtoSubcategory.vtoSubcategoryType ?? "",
                ),
            )
            .pipe(
                map((translation: string) => ({
                    ...vtoSubcategory,
                    localizedVtoSubcategoryType:
                        vtoSubcategory.vtoSubcategoryType
                            ? translation ??
                              this.getVtoTranslationKey(
                                  vtoSubcategory.vtoSubcategoryType,
                              )
                            : "",
                    selectedSkuVariant: NO_SELECT,
                })),
            );
    }

    private getVtoTranslationKey(vtoKeySuffix: string): string {
        return `hermes.product-page.vto-${vtoKeySuffix}`;
    }
}
